import { useState } from 'react';

import { differenceInDays, parse } from 'date-fns';
import PropTypes from 'prop-types';

import { useQuery } from '@zenbusiness/application-commons-graphql';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Checkbox from '@zenbusiness/zui/Checkbox';
import DialogActions from '@zenbusiness/zui/DialogActions';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Divider from '@zenbusiness/zui/Divider';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import Grid from '@zenbusiness/zui/Grid';
import LoadingSpinner from '@zenbusiness/zui/LoadingSpinner';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { useBillingSubscriptionChange } from '#client/hooks/useBillingSubscriptionChange';
import { PAYMENT_METHODS_QUERY } from '#client/hooks/useSubscriptionRestart';

import { useChangeRequestContext, useSetChangeRequest } from '../hooks/useChangeRequestContext';

export default function ChangeBillingIntervalAnnualReview({
  subscription,
  businessEntityName,
  serviceDisplayName,
  products
}) {
  const [changeApproved, setChangeApproved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { formatMessage } = useIntl();

  const { changeFlow } = useChangeRequestContext();
  const { setChangeFlow } = useSetChangeRequest();

  const { interval: selectedInterval, modalFlow } = changeFlow;
  const { changeBillingIntervalAnnual } = modalFlow;
  const serviceName = subscription?.service || '';

  const { data: paymentData, loading: paymentLoading } = useQuery(
    PAYMENT_METHODS_QUERY,
    { noThrow: true }
  );

  const { cancelSubscription: changeSubscription } = useBillingSubscriptionChange();

  const paymentMethod = paymentData?.paymentMethods
    ? (
      paymentData.paymentMethods.methods.find(
        (m) => m.id === paymentData.paymentMethods.defaultMethodId
      ) ?? paymentData.paymentMethods.methods[0]
    )
    : null;

  const cardInfo = !paymentLoading && paymentMethod
    ? `${paymentMethod.brand} x${paymentMethod.last4}`
    : '';

  const selectedProduct = products?.find((p) => p.pricing?.interval === selectedInterval);
  const selectedProductPrice = selectedProduct?.pricing?.amount;

  const currDate = new Date();
  const startOfCycle = [
    (currDate.getMonth() + 1).toString().padStart(2, '0'),
    currDate.getDate().toString().padStart(2, '0'),
    currDate.getFullYear()
  ].join('/');
  const endOfCycle = [
    (currDate.getMonth() + 1).toString().padStart(2, '0'),
    currDate.getDate().toString().padStart(2, '0'),
    currDate.getFullYear() + 1
  ].join('/');

  const creditDays = subscription?.termEndDate
    ? differenceInDays(
      parse(subscription.termEndDate, 'MM/dd/yyyy', new Date()),
      parse(startOfCycle, 'MM/dd/yyyy', new Date())
    )
    : 0;

  const creditAmount = subscription?.remainingProrateValue != null
    ? parseFloat(subscription.remainingProrateValue).toFixed(2)
    : '0.00';

  const totalCharge = selectedProductPrice != null
    ? Math.max(0, selectedProductPrice - parseFloat(creditAmount)).toFixed(2)
    : null;

  const capitalizedInterval = selectedInterval
    ? selectedInterval.charAt(0).toUpperCase() + selectedInterval.slice(1)
    : '';

  const intervalLabel = selectedInterval === 'annual'
    ? formatMessage({ id: 'dashboard.subscriptions.price.periodYear' })
    : formatMessage({ id: 'dashboard.subscriptions.price.periodMonth' });

  const handleSubmit = async () => {
    if (!changeApproved || submitting) return;
    setSubmitting(true);

    try {
      const result = await changeSubscription({
        orderBillingItemUuid: subscription.orderBillingItemUuid,
        changeType: 'billing_interval_change',
        changeDescription: `changing ${serviceName} to ${selectedInterval} billing`
      });
      const statusCode = result?.data?.billingSubscriptionChange?.statusCode;
      if (statusCode === 202) {
        setChangeFlow((prevState) => ({
          ...prevState,
          modalFlow: {
            ...modalFlow,
            changeBillingIntervalAnnual: {
              ...changeBillingIntervalAnnual,
              stepTwo: false,
              stepThree: true
            }
          }
        }));
      } else {
        setChangeFlow((prevState) => ({
          ...prevState,
          modalFlow: {
            ...modalFlow,
            error: true,
            changeBillingIntervalAnnual: {
              ...changeBillingIntervalAnnual,
              stepOne: false,
              stepTwo: false,
              stepThree: false
            }
          }
        }));
      }
    } catch {
      setChangeFlow((prevState) => ({
        ...prevState,
        modalFlow: {
          ...modalFlow,
          error: true,
          changeBillingIntervalAnnual: {
            ...changeBillingIntervalAnnual,
            stepOne: false,
            stepTwo: false,
            stepThree: false
          }
        }
      }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Box mb={3} textAlign="center">
          <Text size="xl" weight="bold" align="center">
            <FormattedMessage id="dashboard.subscriptions.billingChange.reviewTitle" />
          </Text>
        </Box>

        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: '1rem',
            p: 2.5,
            mb: 3
          })}
        >
          <Grid container justifyContent="space-between" flexWrap="nowrap">
            <Grid item>
              <Text size="md" weight="bold">
                {`${serviceDisplayName} (${capitalizedInterval})`}
              </Text>
              <Text size="sm" color="secondary">
                {businessEntityName}
              </Text>
            </Grid>
            <Grid item>
              <Stack direction="row" alignItems="baseline" gap={0.5}>
                <Text size="md" weight="bold">
                  {selectedProductPrice != null ? `$${selectedProductPrice}` : ''}
                </Text>
                <Text size="xs" color="secondary">
                  {`/ ${intervalLabel}`}
                </Text>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <Box>
            <Text size="xs" weight="bold" sx={{ mb: 0.5 }}>
              <FormattedMessage id="dashboard.subscriptions.billingChange.effectiveDate" />
            </Text>
            <Grid container justifyContent="space-between">
              <Grid item xs={12} sm={6}>
                <Text size="sm">
                  {`${startOfCycle} - ${endOfCycle}`}
                </Text>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ textAlign: { sm: 'right' } }}>
                <Text size="xs" color="secondary">
                  <FormattedMessage id="dashboard.subscriptions.billingChange.renewNote" />
                </Text>
              </Grid>
            </Grid>
          </Box>

          <Box mt={2.5}>
            <Text size="xs" weight="bold" sx={{ mb: 0.5 }}>
              <FormattedMessage id="dashboard.subscriptions.billingChange.paymentMethod" />
            </Text>
            <Text size="sm">{cardInfo}</Text>
          </Box>

          {subscription?.remainingProrateValue != null
            && subscription.remainingProrateValue !== 0 && (
            <Box mt={2.5}>
              <Text size="xs" weight="bold" sx={{ mb: 0.5 }}>
                <FormattedMessage id="dashboard.subscriptions.billingChange.credits" />
              </Text>
              <Grid container justifyContent="space-between" flexWrap="nowrap">
                <Grid item xs={6}>
                  <Text size="sm">
                    <FormattedMessage
                      id="dashboard.subscriptions.billingChange.creditApplied"
                      values={{ days: creditDays }}
                    />
                  </Text>
                  <Text size="sm">
                    {`${startOfCycle} - ${subscription.termEndDate}`}
                  </Text>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                  <Text size="sm" color="secondary">
                    {`-$${creditAmount}`}
                  </Text>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            bgcolor: 'background.default',
            borderRadius: '1rem',
            p: 2,
            mb: 3
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Text size="sm">
                <FormattedMessage id="dashboard.subscriptions.billingChange.totalDueToday" />
              </Text>
            </Grid>
            <Grid item>
              <Text size="lg" weight="bold">
                {totalCharge != null ? `$${totalCharge} USD` : ''}
              </Text>
            </Grid>
          </Grid>
        </Box>

        <Stack gap={0.5} sx={{ mb: 2 }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={changeApproved}
                onChange={(e) => setChangeApproved(e.target.checked)}
                data-testid="check-billing-schedule"
              />
            )}
            label={(
              <Text component="span" size="sm" weight="bold">
                <FormattedMessage id="dashboard.subscriptions.billingChange.consentCheckbox" />
              </Text>
            )}
            sx={{ ml: 0 }}
          />
          <Box pl={4}>
            <Text size="xs" color="tertiary">
              <FormattedMessage id="dashboard.subscriptions.renewalConsent" />
            </Text>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3 }}>
        <Button
          variant="contained"
          disabled={!changeApproved || submitting}
          onClick={handleSubmit}
          clickstreamProps={{ elementName: 'billing_change_proceed' }}
          data-testid="proceed-with-billing-change"
        >
          {submitting
            ? <LoadingSpinner size={20} aria-label="Submitting" />
            : <FormattedMessage id="dashboard.subscriptions.billingChange.proceed" />}
        </Button>
      </DialogActions>
    </>
  );
}

ChangeBillingIntervalAnnualReview.propTypes = {
  subscription: PropTypes.shape({
    orderBillingItemUuid: PropTypes.string,
    service: PropTypes.string,
    termEndDate: PropTypes.string,
    remainingProrateValue: PropTypes.number
  }).isRequired,
  businessEntityName: PropTypes.string,
  serviceDisplayName: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.shape({
    pricing: PropTypes.shape({
      interval: PropTypes.string,
      amount: PropTypes.number
    })
  }))
};
